import { Container, Button, Modal, Form, ProgressBar } from "react-bootstrap";
import { useState, useEffect } from "react";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useSelector } from "react-redux";
import { publishImage } from "@/helpers/imageupload";
import { getAsset } from "@/helpers/helper";
import { SuccessNotify, FailedNotify } from "@/constants/tostify";
import { useDropzone } from "react-dropzone";
import { useCreatePostMutation } from "@/features/timeline/timelineApiSlice";

const UploadPostModal = ({
  show,
  handleClose,
  CreatePostSubmit,
  createPost,
  setCreatePost,
  datacheck,
}) => {
  const [disabled, setDisabled] = useState(true);

  // Upload states
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);
  const [selectedPreviewVideo, setSelectedPreviewVideo] = useState(null);
  const { name } = useSelector((state) => state.user || {});

  const handleValue = (e) => {
    const { name: fieldName, value } = e.target;
    if (typeof setCreatePost === "function") {
      setCreatePost((prev) => ({
        ...prev,
        my: prev?.my?.map((item) => ({
          ...item,
          [fieldName]: value,
        })),
      }));
    }
  };

  useEffect(() => {
    const headingFilled = !!createPost?.my?.some(
      (item) => item.heading && item.heading.trim() !== "",
    );
    const contentFilled = !!createPost?.my?.some(
      (item) => item.content && item.content.trim() !== "",
    );

    setDisabled(!(headingFilled && contentFilled));
  }, [createPost]);

  const onDropport = async (acceptedFiles) => {
    try {
      setUploading(true);
      setProgress(0);

      // show local preview immediately
      let localObjectUrl = null;
      if (acceptedFiles && acceptedFiles[0]) {
        localObjectUrl = URL.createObjectURL(acceptedFiles[0]);
        setSelectedPreviewImage(localObjectUrl);
      }

      const img = await publishImage(acceptedFiles, (percent) => {
        setProgress(percent);
      });

      const fullUrls = (img || []).map((p) => getAsset(p));

      if (typeof setCreatePost === "function") {
        setCreatePost((prev) => ({
          ...prev,
          my: prev?.my?.map((item) => ({
            ...item,
            img,
          })),
        }));
      }

      if (fullUrls.length > 0) setSelectedPreviewImage(fullUrls[0]);

      SuccessNotify("Image uploaded successfully");
    } catch (error) {
      console.error("Image upload error:", error);
      FailedNotify("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDropvideo = async (acceptedFiles) => {
    try {
      setUploading(true);
      setProgress(0);

      let localObjectUrl = null;
      if (acceptedFiles && acceptedFiles[0]) {
        localObjectUrl = URL.createObjectURL(acceptedFiles[0]);
        setSelectedPreviewVideo(localObjectUrl);
      }

      const video = await publishImage(acceptedFiles, (percent) => {
        setProgress(percent);
      });

      const fullVideoUrls = (video || []).map((p) => getAsset(p));

      if (typeof setCreatePost === "function") {
        setCreatePost((prev) => ({
          ...prev,
          my: prev?.my?.map((item) => ({
            ...item,
            video,
          })),
        }));
      }

      if (fullVideoUrls.length > 0) setSelectedPreviewVideo(fullVideoUrls[0]);

      SuccessNotify("Video uploaded successfully");
    } catch (error) {
      console.error("Video upload error:", error);
      FailedNotify("Video upload failed");
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps: getRootPropsPort, getInputProps: getInputPropsPort } =
    useDropzone({
      accept: { "image/*": [] },
      onDrop: onDropport,
      disabled: uploading,
    });

  const { getRootProps: getRootPropsVideo, getInputProps: getInputPropsVideo } =
    useDropzone({
      accept: { "video/*": [] },
      onDrop: onDropvideo,
      disabled: uploading,
    });

  useEffect(() => {
    if (show) {}

    return () => {
      if (selectedPreviewImage && selectedPreviewImage.startsWith("blob:")) {
        URL.revokeObjectURL(selectedPreviewImage);
      }
      if (selectedPreviewVideo && selectedPreviewVideo.startsWith("blob:")) {
        URL.revokeObjectURL(selectedPreviewVideo);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, datacheck]);

  // RTK mutation (fallback if parent doesn't call CreatePostSubmit)
  const [createPostRtk, { isLoading: isCreating }] = useCreatePostMutation();

  const handlePost = async () => {
    const payloadToSend = createPost || {
      shopid: "",
      userid: name?._id || "",
      businessType: "generalPublic",
      my: [
        {
          heading: "",
          content: "",
          img: [""],
          video: [""],
        },
      ],
    };

    console.debug("Uploadpost - submit payload:", payloadToSend);

    if (CreatePostSubmit && typeof CreatePostSubmit === "function") {
      try {
        CreatePostSubmit();
      } catch (err) {
        console.error("Parent CreatePostSubmit error:", err);
      }
      return;
    }

    try {
      const res = await createPostRtk(payloadToSend).unwrap();
      console.debug("Uploadpost - createPost response:", res);
      SuccessNotify("Post Created Successfully...");
      handleClose();
    } catch (err) {
      console.error("Uploadpost - createPost error:", err);
      FailedNotify(
        err?.data?.message || err?.message || "Failed to create post",
      );
    }
  };

  return (
    <Container>
      <Modal show={show} onHide={handleClose} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h6 className="m-0">Upload Content</h6>
          </Modal.Title>
        </Modal.Header>

        <Form.Group className="mt-3 ps-3 pe-3">
          <Form.Control
            type="text"
            placeholder="Title..."
            onChange={handleValue}
            name="heading"
          />
        </Form.Group>

        <Modal.Body>
          <textarea
            className="form-control"
            rows="3"
            name="content"
            placeholder="Write something..."
            onChange={handleValue}
          />

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-3">
              <small>Uploading...</small>
              <ProgressBar
                now={progress}
                label={`${progress}%`}
                striped
                animated
              />
            </div>
          )}

          {/* Icons */}
          <div className="d-flex justify-content-end mt-3">
            <div
              className={`text-warning me-2 ${uploading ? "opacity-50" : ""}`}
              {...getRootPropsPort()}
            >
              <CameraAltIcon />
              <input {...getInputPropsPort()} />
            </div>

            <div
              className={`text-success ${uploading ? "opacity-50" : ""}`}
              {...getRootPropsVideo()}
            >
              <VideocamIcon />
              <input {...getInputPropsVideo()} />
            </div>
          </div>

          {/* Preview (render after textarea and icons to avoid overlapping) */}
          {(selectedPreviewImage || selectedPreviewVideo) && (
            <div className="mt-3 mb-3 text-center">
              {selectedPreviewImage && (
                <img
                  src={selectedPreviewImage}
                  alt="preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 240,
                    objectFit: "contain",
                  }}
                />
              )}
              {selectedPreviewVideo && (
                <video
                  src={selectedPreviewVideo}
                  controls
                  style={{ maxWidth: "100%", maxHeight: 240 }}
                />
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <Button onClick={handleClose} className="cancle-btn">
            Cancel
          </Button>

          <Button
            className="post-btn"
            disabled={disabled || uploading || isCreating}
            onClick={handlePost}
          >
            {uploading || isCreating ? "Uploading..." : "Post"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UploadPostModal;
